import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Trash2, Plus } from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { deleteTag } from "@/lib/stores/tagsStore";
import { TagItem } from "@/lib/stores/tagsStore";
import TagBadge from "./TagBadge";
import AddTagsToGroup from "./AddTagsToGroup";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TagListProps {
  groupedTags: Record<string, TagItem[]>;
  isLoading: boolean;
  onRefresh: () => void;
}



export default function TagList({ groupedTags, isLoading, onRefresh }: TagListProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();

  // State for collapsible groups (all collapsed by default)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(
    Object.keys(groupedTags).reduce((acc, group) => {
      acc[group] = true; // Default collapsed
      return acc;
    }, {} as Record<string, boolean>)
  );

  // State for managing which group is being added to
  const [addingToGroup, setAddingToGroup] = useState<string | null>(null);

  // Handle opening add tags form - expand target group, collapse others, close any existing form
  const handleAddTagsToGroup = (targetGroup: string) => {
    // Close any existing add tags form
    setAddingToGroup(null);
    
    // Collapse all groups except the target group
    const newCollapsedState: Record<string, boolean> = {};
    Object.keys(groupedTags).forEach(group => {
      newCollapsedState[group] = group !== targetGroup; // Only keep target group expanded
    });
    setCollapsedGroups(newCollapsedState);
    
    // Open the add tags form for the target group
    setAddingToGroup(targetGroup);
  };



  // Toggle group collapse state
  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Delete entire group
  const handleDeleteGroup = async (group: string, groupTags: TagItem[]) => {
    if (!selectedGame) return;

    try {
      // Delete all tags in the group
      const deletePromises = groupTags.map(tag => deleteTag(tag.id, selectedGame.id, toast));
      await Promise.all(deletePromises);
      
      toast({
        title: "Success",
        description: `Deleted group "${group}" with ${groupTags.length} tag${groupTags.length > 1 ? 's' : ''}`,
      });
      
      onRefresh();
    } catch (error) {
      console.error("Error deleting group:", error);
      toast({
        title: "Error",
        description: "Failed to delete group. Please try again.",
        variant: "destructive",
      });
    }
  };



  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">{t('platform.cards.widgets.loading_tags')}</p>
      </div>
    );
  }

  if (Object.keys(groupedTags).length === 0) {
    return (
      <div className="text-center py-8">
          <p className="text-muted-foreground">{t('platform.cards.widgets.no_tags_created_yet')}</p>
        <p className="text-xs text-muted-foreground mt-1">{t('platform.cards.widgets.create_your_first_tag_group_to_get_started')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(groupedTags).map(([group, groupTags]) => {
        const isCollapsed = collapsedGroups[group] ?? true;
        
        return (
          <div key={group} className="space-y-2">
            {/* Group Header */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => toggleGroup(group)}
                className="flex items-center gap-2 hover:text-foreground transition-colors"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                )}
                <span className="text-sm font-medium text-foreground">
                  {group === "Ungrouped" ? "Ungrouped" : group}
                </span>
                <span className="text-xs text-muted-foreground ml-1">
                  ({groupTags.length})
                </span>
              </button>
              
              <div className="flex items-center gap-1">
                {/* Add Tags Button - only show for grouped tags */}
                {group !== "Ungrouped" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddTagsToGroup(group)}
                    className="h-6 px-2 text-xs text-primary hover:text-primary-foreground hover:bg-primary"
                    title={`Add more tags to "${group}"`}
                  >
                    Add Tags
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteGroup(group, groupTags)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive"
                  title={`Delete entire group "${group}"`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Group Content */}
            {!isCollapsed && (
              <div className="ml-5">
                <div className="flex flex-wrap gap-2">
                  {groupTags.map((tag) => (
                    <TagBadge
                      key={tag.id}
                      tag={tag}
                      onRefresh={onRefresh}
                    />
                  ))}
                </div>
                
                {/* Add Tags to Group Component */}
                {addingToGroup === group && (
                  <div className="mt-3">
                    <AddTagsToGroup
                      isOpen={true}
                      onClose={() => setAddingToGroup(null)}
                      onSuccess={() => {
                        onRefresh();
                        setAddingToGroup(null);
                        // Keep the group expanded to show the newly added tags
                      }}
                      groupName={group}
                      groupColor={groupTags[0]?.color || "#6366f1"}
                      groupDescription={groupTags[0]?.description}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
